import React, { useState } from 'react';
import { UserProfile, DailyPlan, Recipe, Ingredient } from '../../../app/domain/types/index.ts';
import { Calendar, ChevronLeft, ChevronRight, Clock, Flame, Check, Printer, Share2, ShoppingCart, ArrowRight, Utensils, RefreshCw, Star, Info, X, ShoppingBag, ArrowLeftRight } from 'lucide-react';

interface MealPlannerProps {
  user: UserProfile;
}

// Mock Recipes
const recipeDb: Record<string, Recipe> = {
  'r1': {
    id: 'r1',
    title: 'Overnight Oats with Berries',
    description: 'A heart-healthy, low-GI breakfast option perfect for meal prep.',
    calories: 400,
    macros: { protein: 15, carbs: 55, fat: 12 },
    prepTime: 5,
    totalTime: 480, // 8 hours
    tags: ['High Fiber', 'Heart Healthy'],
    rating: 4.9,
    reviewCount: 127,
    priceEstimate: 3.50,
    ingredients: [
      { item: 'Rolled Oats', quantity: '1/2', unit: 'cup', category: 'Pantry' },
      { item: 'Almond Milk', quantity: '1/2', unit: 'cup', category: 'Dairy/Alt' },
      { item: 'Chia Seeds', quantity: '1', unit: 'tbsp', category: 'Pantry' },
      { item: 'Mixed Berries', quantity: '1/2', unit: 'cup', category: 'Produce' }
    ],
    instructions: [
      'Combine oats, milk, and chia seeds in a jar.',
      'Stir well and refrigerate overnight (at least 4 hours).',
      'In the morning, top with fresh berries.',
      'Enjoy cold.'
    ]
  },
  'r2': {
    id: 'r2',
    title: 'Grilled Chicken Salad',
    description: 'Packed with lean protein and fresh vegetables.',
    calories: 450,
    macros: { protein: 45, carbs: 12, fat: 20 },
    prepTime: 15,
    totalTime: 25,
    tags: ['High Protein', 'Low Carb'],
    rating: 4.7,
    reviewCount: 89,
    priceEstimate: 6.20,
    ingredients: [
      { item: 'Chicken Breast', quantity: '6', unit: 'oz', category: 'Protein' },
      { item: 'Mixed Greens', quantity: '3', unit: 'cups', category: 'Produce' },
      { item: 'Cherry Tomatoes', quantity: '1/2', unit: 'cup', category: 'Produce' },
      { item: 'Olive Oil', quantity: '1', unit: 'tbsp', category: 'Pantry' }
    ],
    instructions: [
      'Season chicken breast with salt, pepper, and herbs.',
      'Grill for 6-8 minutes per side until cooked through.',
      'Toss greens and tomatoes with olive oil.',
      'Slice chicken and serve over salad.'
    ]
  },
  'r3': {
    id: 'r3',
    title: 'Baked Salmon with Asparagus',
    description: 'Rich in Omega-3 fatty acids and fiber.',
    calories: 520,
    macros: { protein: 35, carbs: 8, fat: 32 },
    prepTime: 10,
    totalTime: 30,
    tags: ['Keto Friendly', 'Gluten Free'],
    rating: 4.8,
    reviewCount: 210,
    priceEstimate: 8.50,
    ingredients: [
      { item: 'Salmon Fillet', quantity: '6', unit: 'oz', category: 'Protein' },
      { item: 'Asparagus', quantity: '1', unit: 'bunch', category: 'Produce' },
      { item: 'Lemon', quantity: '1/2', unit: 'fruit', category: 'Produce' },
      { item: 'Butter', quantity: '1', unit: 'tbsp', category: 'Dairy' }
    ],
    instructions: [
      'Preheat oven to 400°F (200°C).',
      'Place salmon and asparagus on a baking sheet.',
      'Top with butter and lemon slices.',
      'Bake for 12-15 minutes.'
    ]
  }
};

// Mock Weekly Plan
const mockWeeklyPlan: DailyPlan[] = [
  {
    date: '2025-12-08',
    dayName: 'Mon',
    dailyCalories: 1850,
    meals: [
      { id: 'm1', type: 'breakfast', recipe: recipeDb['r1'], completed: true },
      { id: 'm2', type: 'lunch', recipe: recipeDb['r2'], completed: false },
      { id: 'm3', type: 'dinner', recipe: recipeDb['r3'], completed: false }
    ]
  },
  {
    date: '2025-12-09',
    dayName: 'Tue',
    dailyCalories: 1800,
    meals: [
      { id: 'm4', type: 'breakfast', recipe: recipeDb['r1'], completed: false },
      { id: 'm5', type: 'lunch', recipe: recipeDb['r2'], completed: false },
      { id: 'm6', type: 'dinner', recipe: recipeDb['r3'], completed: false }
    ]
  },
  {
    date: '2025-12-10',
    dayName: 'Wed',
    dailyCalories: 1900,
    meals: [
      { id: 'm7', type: 'breakfast', recipe: recipeDb['r1'], completed: false },
      { id: 'm8', type: 'lunch', recipe: recipeDb['r2'], completed: false },
      { id: 'm9', type: 'dinner', recipe: recipeDb['r3'], completed: false }
    ]
  },
];

const MealPlanner: React.FC<MealPlannerProps> = ({ user }) => {
  const [view, setView] = useState<'weekly' | 'recipe' | 'shopping'>('weekly');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Derived state for shopping list
  const generateShoppingList = () => {
    const allIngredients: Ingredient[] = [];
    mockWeeklyPlan.forEach(day => {
      day.meals.forEach(meal => {
        allIngredients.push(...meal.recipe.ingredients);
      });
    });

    // Group by category (simplified)
    const grouped: Record<string, Ingredient[]> = {};
    allIngredients.forEach(ing => {
      if (!grouped[ing.category]) grouped[ing.category] = [];
      grouped[ing.category].push(ing);
    });
    return grouped;
  };

  const shoppingList = generateShoppingList();

  const handleRecipeClick = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setView('recipe');
  };

  const currentDay = mockWeeklyPlan[selectedDayIndex];

  // Component for Day Scroller
  const DayScroller = () => (
    <div className="flex items-center justify-between mt-4 bg-stone-100 dark:bg-stone-800 p-1 rounded-2xl">
        <button
            onClick={() => setSelectedDayIndex(Math.max(0, selectedDayIndex - 1))}
            disabled={selectedDayIndex === 0}
            className="p-2 rounded-xl hover:bg-white dark:hover:bg-stone-700 disabled:opacity-30 transition-colors"
        >
            <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
        <div className="flex gap-1 md:gap-4 overflow-x-auto no-scrollbar px-2">
            {mockWeeklyPlan.map((day, idx) => (
                <button
                    key={idx}
                    onClick={() => setSelectedDayIndex(idx)}
                    className={`flex flex-col items-center justify-center w-14 h-16 rounded-xl transition-all ${
                        idx === selectedDayIndex 
                        ? 'bg-white dark:bg-stone-700 shadow-sm text-health-600 dark:text-health-400 font-bold border border-stone-200 dark:border-stone-600' 
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                    }`}
                >
                    <span className="text-xs uppercase tracking-wider">{day.dayName}</span>
                    <span className="text-lg">{day.date.split('-')[2]}</span>
                </button>
            ))}
        </div>
        <button
            onClick={() => setSelectedDayIndex(Math.min(mockWeeklyPlan.length - 1, selectedDayIndex + 1))}
            disabled={selectedDayIndex === mockWeeklyPlan.length - 1}
            className="p-2 rounded-xl hover:bg-white dark:hover:bg-stone-700 disabled:opacity-30 transition-colors"
        >
            <ChevronRight size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
    </div>
  );

  // Component for Recipe Details
  const RecipeDetailView = ({ recipe }: { recipe: Recipe }) => (
    <div className="animate-fade-in-up h-full overflow-y-auto no-scrollbar">
        <div className="md:hidden mb-4">
             <button onClick={() => setView('weekly')} className="flex items-center gap-2 text-sm text-gray-500"><ChevronLeft size={16}/> Back</button>
        </div>
        <div className="glass-panel overflow-hidden rounded-[2rem] bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 shadow-xl h-full flex flex-col">
            <div className="bg-gradient-to-r from-stone-100 to-white dark:from-stone-800 dark:to-stone-900 p-8 border-b border-stone-100 dark:border-stone-800 relative">
                 <div className="flex gap-2 mb-4">
                    {recipe.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-white dark:bg-stone-700 shadow-sm rounded-full text-xs font-bold text-health-600 dark:text-health-400 uppercase tracking-wide">{tag}</span>
                    ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-2 tracking-tight">{recipe.title}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{recipe.description}</p>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
                 <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="text-center p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
                        <Clock size={20} className="text-health-500 mx-auto mb-1" />
                        <span className="font-bold text-gray-900 dark:text-white block">{recipe.totalTime}m</span>
                        <span className="text-[10px] text-gray-500 uppercase">Time</span>
                    </div>
                    <div className="text-center p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
                        <Flame size={20} className="text-orange-500 mx-auto mb-1" />
                        <span className="font-bold text-gray-900 dark:text-white block">{recipe.calories}</span>
                        <span className="text-[10px] text-gray-500 uppercase">Cals</span>
                    </div>
                    <div className="text-center p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
                        <Star size={20} className="text-yellow-400 fill-yellow-400 mx-auto mb-1" />
                        <span className="font-bold text-gray-900 dark:text-white block">{recipe.rating}</span>
                        <span className="text-[10px] text-gray-500 uppercase">Rating</span>
                    </div>
                 </div>

                 <h3 className="font-bold text-gray-900 dark:text-white mb-4">Ingredients</h3>
                 <ul className="space-y-3 mb-8">
                    {recipe.ingredients.map((ing, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-health-500"></div>
                            <span className="text-gray-700 dark:text-gray-300">{ing.quantity} {ing.unit} {ing.item}</span>
                        </li>
                    ))}
                 </ul>

                 <h3 className="font-bold text-gray-900 dark:text-white mb-4">Instructions</h3>
                 <div className="space-y-4">
                    {recipe.instructions.map((step, i) => (
                        <div key={i} className="flex gap-4">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</span>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{step}</p>
                        </div>
                    ))}
                 </div>
            </div>
        </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4">
            <div>
                <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">Meal Planning</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Personalized for <span className="font-medium text-gray-900 dark:text-white">{user.goal}</span>
                </p>
            </div>
            <div className="flex gap-3">
                 <button
                    onClick={() => setView('shopping')}
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${view === 'shopping' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-gray-700 dark:text-gray-200 hover:bg-stone-50 dark:hover:bg-stone-700'}`}
                 >
                    <ShoppingCart size={18} /> List
                 </button>
            </div>
        </div>
        {view !== 'shopping' && <DayScroller />}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative">

        {/* VIEW: Shopping List (Fullscreen overlay) */}
        {view === 'shopping' && (
             <div className="absolute inset-0 z-30 bg-[#FAFAFA] dark:bg-[#0f0f0f] p-6 overflow-y-auto animate-fade-in-up">
                 <div className="max-w-2xl mx-auto">
                    <button
                        onClick={() => setView('weekly')}
                        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6"
                    >
                        <ChevronLeft size={16} /> Back to Plan
                    </button>
                    <div className="glass-panel p-8 rounded-[2rem] bg-white/80 dark:bg-stone-900/80 border-stone-100 dark:border-stone-700 shadow-xl">
                        <div className="flex justify-between items-end mb-8">
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Shopping List</h2>
                            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><Share2 size={20} /></button>
                        </div>
                        {Object.entries(shoppingList).map(([category, ingredients]) => (
                            <div key={category} className="mb-6 last:mb-0">
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{category}</h3>
                                <div className="space-y-1">
                                    {ingredients.map((ing, idx) => (
                                        <label key={idx} className="flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors cursor-pointer">
                                            <input type="checkbox" className="w-5 h-5 rounded-md text-health-600 bg-white dark:bg-stone-700 border-gray-300" />
                                            <span className="flex-1 font-medium text-gray-800 dark:text-gray-200">{ing.item}</span>
                                            <span className="text-xs text-gray-500 bg-stone-100 dark:bg-stone-700 px-2 py-0.5 rounded">{ing.quantity} {ing.unit}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="mt-8 pt-6 border-t border-stone-200 dark:border-stone-700">
                            <button className="w-full py-4 bg-health-600 text-white rounded-xl font-bold shadow-lg flex justify-center items-center gap-2 hover:bg-health-700 transition-colors">
                                Checkout with Instacart <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                 </div>
             </div>
        )}

        {/* VIEW: Split Pane (List + Detail) */}
        <div className="h-full flex">

            {/* Left Pane: Meal List (Visible on mobile if view==weekly, always visible on desktop) */}
            <div className={`w-full md:w-1/2 lg:w-5/12 h-full overflow-y-auto p-6 md:p-8 no-scrollbar ${view === 'recipe' ? 'hidden md:block' : 'block'}`}>
                <div className="space-y-6">
                    {/* Daily Summary */}
                    <div className="glass-panel p-6 rounded-3xl bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    {currentDay.dayName}, Dec {currentDay.date.split('-')[2]}
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Goal: {currentDay.dailyCalories} kcal</p>
                            </div>
                            <button className="flex items-center gap-2 text-sm font-medium text-health-600 dark:text-health-400 hover:underline">
                                <RefreshCw size={16} /> Regenerate
                            </button>
                        </div>

                        <div className="space-y-4">
                            {currentDay.meals.map((meal) => (
                                <div
                                    key={meal.id}
                                    onClick={() => handleRecipeClick(meal.recipe)}
                                    className={`group relative bg-white dark:bg-stone-800 rounded-2xl border shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${selectedRecipe?.id === meal.recipe.id ? 'border-health-500 ring-2 ring-health-500/20' : 'border-stone-100 dark:border-stone-700 hover:border-health-200 dark:hover:border-health-800'}`}
                                >
                                    <div className="absolute top-4 right-4 z-10">
                                         <button onClick={(e) => { e.stopPropagation(); /* Logic for swapping */ }} className="p-2 bg-stone-100 dark:bg-stone-700 rounded-full hover:bg-white dark:hover:bg-stone-600 transition-colors text-gray-500 dark:text-gray-300 shadow-sm" title="Swap Meal">
                                            <ArrowLeftRight size={14} />
                                         </button>
                                    </div>
                                    <div className="h-full flex flex-row">
                                        <div className={`w-2 h-full absolute left-0 top-0 bottom-0 ${meal.type === 'breakfast' ? 'bg-orange-400' : meal.type === 'lunch' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                                        <div className="pl-6 p-4 flex-1">
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{meal.type}</span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2 group-hover:text-health-600 dark:group-hover:text-health-400 transition-colors">{meal.recipe.title}</h3>
                                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                                <span className="flex items-center gap-1"><Flame size={14} /> {meal.recipe.calories}</span>
                                                <span className="flex items-center gap-1"><Clock size={14} /> {meal.recipe.prepTime}m</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Pane: Detail (Visible on mobile if view==recipe, always visible on desktop if selected) */}
            <div className={`w-full md:w-1/2 lg:w-7/12 h-full bg-stone-50 dark:bg-black/20 p-6 md:p-8 border-l border-stone-200 dark:border-stone-800 ${view === 'recipe' ? 'block' : 'hidden md:block'}`}>
                {selectedRecipe ? (
                    <RecipeDetailView recipe={selectedRecipe} />
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                        <Utensils size={48} className="mb-4 opacity-20" />
                        <p className="text-sm">Select a meal to view details</p>
                    </div>
                )}
            </div>
        </div>

        {/* FAB for Shopping List (Desktop/Mobile) */}
        <button
            onClick={() => setView('shopping')}
            className="absolute bottom-6 right-6 md:right-12 z-40 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-4 rounded-full font-bold shadow-2xl hover:scale-105 transition-transform flex items-center gap-2"
        >
            <ShoppingBag size={20} /> <span className="hidden md:inline">Shop This Week</span>
        </button>

      </div>
    </div>
  );
};

export default MealPlanner;